/**
 * SF-4779 — two-step sign-in admin API client (epic SF-4775, sf-api SF-4777).
 *
 *   GET  /admin/v1/companies/:id/two-step                     perm admin:companies:read
 *        ?limit (default 25, max 100) &offset &enabled=true|false &search=
 *        → { required, available, users: [{ _id, name, email, type, two_step: { enabled, method, enabled_at } }], totalCount }
 *   POST /admin/v1/companies/:id/users/:userId/two-step/reset perm admin:companies:write
 *        → { reset: true } (404 when the user is not in that company;
 *          403 { code: TWO_STEP_RESET_SELF } for the staff member's own account;
 *          409 { code: TWO_STEP_CHANGED } when their two-step changed meanwhile)
 *
 * `available` mirrors the platform flag two_step_signin_available (contract
 * v3). A reply without it counts as false: the safe reading, since requiring
 * two-step while the audit app cannot enter codes locks people out.
 *
 * The company switch itself (`twoFactorAuthentication`) goes through the
 * normal admin company PATCH (useSaveCompanyField).
 */
import { useCustom, useCustomMutation, type BaseRecord } from '@refinedev/core';
import type { AxiosError } from 'axios';
import { COMPANIES_API_BASE } from './companyDeleteClient';

export const TWO_STEP_FIELD = 'twoFactorAuthentication';
export const TWO_STEP_PAGE_SIZE = 25;

export type TwoStepMethod = 'app' | 'email';

export interface TwoStepUser {
  _id: string;
  name?: string;
  email?: string;
  type?: string;
  two_step?: { enabled?: boolean; method?: TwoStepMethod | null; enabled_at?: string | null };
}

export interface TwoStepList {
  required: boolean;
  /** Platform flag two_step_signin_available; false when the reply leaves it out. */
  available: boolean;
  users: TwoStepUser[];
  totalCount: number;
}

export interface TwoStepListQuery {
  page: number;
  enabled?: 'true' | 'false';
  search?: string;
}

export function twoStepListUrl(companyId: string, q: TwoStepListQuery): string {
  const params = new URLSearchParams();
  params.set('limit', String(TWO_STEP_PAGE_SIZE));
  params.set('offset', String((Math.max(1, q.page) - 1) * TWO_STEP_PAGE_SIZE));
  if (q.enabled) params.set('enabled', q.enabled);
  const search = q.search?.trim();
  if (search) params.set('search', search);
  return `/${COMPANIES_API_BASE}/${encodeURIComponent(companyId)}/two-step?${params.toString()}`;
}

export function twoStepResetUrl(companyId: string, userId: string): string {
  return `/${COMPANIES_API_BASE}/${encodeURIComponent(companyId)}/users/${encodeURIComponent(userId)}/two-step/reset`;
}

/** Accepts the body with or without the `{ success, data }` envelope. */
export function parseTwoStepList(payload: unknown): TwoStepList | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  const inner = Array.isArray(p.users) ? p : (p.data as Record<string, unknown> | undefined);
  if (!inner || !Array.isArray(inner.users)) return null;
  return {
    required: inner.required === true,
    available: inner.available === true,
    users: inner.users as TwoStepUser[],
    totalCount: typeof inner.totalCount === 'number' ? inner.totalCount : inner.users.length,
  };
}

export function useTwoStepUsers(companyId: string, q: TwoStepListQuery) {
  return useCustom<BaseRecord>({
    url: twoStepListUrl(companyId, q),
    method: 'get',
    queryOptions: {
      enabled: !!companyId,
      refetchOnWindowFocus: false,
      retry: false,
      // Keep the last rows on screen while the next page/filter loads or fails.
      keepPreviousData: true,
    },
    errorNotification: false,
  });
}

export function useTwoStepReset() {
  return useCustomMutation<BaseRecord>();
}

export function twoStepResetArgs(companyId: string, userId: string) {
  return {
    url: twoStepResetUrl(companyId, userId),
    method: 'post' as const,
    values: {},
    errorNotification: false as const,
    successNotification: false as const,
  };
}

export const TWO_STEP_RESET_SELF_TEXT = 'You cannot reset your own two-step sign-in here.';
// Shown before the refetch settles, so it must not claim the list is already
// fresh; a failed refetch adds the table's could-not-refresh note.
export const TWO_STEP_CHANGED_TEXT =
  "This person's two-step changed while you were looking. Refreshing the list.";

export function isTwoStepNotAvailable(error: unknown): boolean {
  const e = error as AxiosError<{ code?: string }> | undefined;
  return e?.response?.status === 403 && e?.response?.data?.code === 'TWO_STEP_NOT_AVAILABLE';
}

export function twoStepErrorMessage(error: unknown, fallback: string): string {
  const e = error as AxiosError<{ code?: string; message?: string }> | undefined;
  const status = e?.response?.status;
  const code = e?.response?.data?.code;
  if (code === 'TWO_STEP_RESET_SELF') return TWO_STEP_RESET_SELF_TEXT;
  if (code === 'TWO_STEP_CHANGED') return TWO_STEP_CHANGED_TEXT;
  if (status === 403) return 'You do not have permission to do this (admin:companies:write).';
  if (status === 404) return 'This person is not in this company any more.';
  return e?.response?.data?.message ?? fallback;
}
